import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useAdminRole } from '../../../hooks/useAdminRole';
import { useSystemSettings } from '../../../hooks/useSystemSettings';

interface AdminSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface NavItem {
  icon: string;
  label: string;
  path: string;
  module: string;
}

const mainNavItems: NavItem[] = [
  { icon: 'ri-dashboard-3-line',     label: 'Dashboard',        path: '/admin',                  module: 'dashboard'       },
  { icon: 'ri-line-chart-line',      label: 'Visão Executiva',  path: '/admin/visao-executiva',  module: 'dashboard'       },
  { icon: 'ri-group-line',           label: 'Usuários',         path: '/admin/usuarios',         module: 'usuarios'        },
  { icon: 'ri-price-tag-3-line',   label: 'Planos',       path: '/admin/planos',       module: 'planos'      },
  { icon: 'ri-file-list-3-line',   label: 'Assinaturas',  path: '/admin/assinaturas',  module: 'assinaturas' },
  { icon: 'ri-bank-card-line',     label: 'Pagamentos',   path: '/admin/pagamentos',   module: 'pagamentos'  },
  { icon: 'ri-feedback-line',      label: 'Feedbacks',    path: '/admin/feedbacks',    module: 'feedbacks'   },
  { icon: 'ri-lightbulb-flash-line', label: 'Sugestões',  path: '/admin/sugestoes',    module: 'sugestoes'   },
  { icon: 'ri-megaphone-line',     label: 'Comunicados',  path: '/admin/comunicados',  module: 'comunicados' },
];

const configNavItems: NavItem[] = [
  { icon: 'ri-robot-2-line',         label: 'Panda AI',          path: '/admin/panda-ai',          module: 'integracoes'     },
  { icon: 'ri-secure-payment-line',  label: 'Gateways',          path: '/admin/gateways',          module: 'gateways'        },
  { icon: 'ri-plug-2-line',          label: 'Integrações',       path: '/admin/integracoes',       module: 'integracoes'     },
  { icon: 'ri-webhook-line',         label: 'Webhooks',          path: '/admin/webhooks',          module: 'webhooks'        },
  { icon: 'ri-shield-user-line',     label: 'Administradores',   path: '/admin/administradores',   module: 'administradores' },
  { icon: 'ri-settings-3-line',      label: 'Configurações',     path: '/admin/configuracoes',     module: 'configuracoes'   },
  { icon: 'ri-shield-check-line',    label: 'Auditoria',         path: '/admin/auditoria',         module: 'auditoria'       },
];

const roleLabel: Record<string, { label: string; color: string }> = {
  master_admin: { label: 'Master Admin', color: 'text-violet-400' },
  admin:        { label: 'Admin',        color: 'text-indigo-400' },
  support:      { label: 'Suporte',      color: 'text-cyan-400'   },
  finance:      { label: 'Financeiro',   color: 'text-emerald-400'},
  customer:     { label: 'Cliente',      color: 'text-white/40'   },
};

export const AdminSidebar = ({ isCollapsed, onToggle, mobileOpen = false, onMobileClose }: AdminSidebarProps) => {
  const { signOut, profile, role } = useAuth();
  const { can } = useAdminRole();
  const navigate = useNavigate();
  const { settings } = useSystemSettings();

  const platformName = settings.platform_name || '';
  const adminPrimary = settings.admin_primary_color || '#7c3aed';

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const visibleMain   = mainNavItems.filter((i) => can(i.module as any));
  const visibleConfig = configNavItems.filter((i) => can(i.module as any));
  const roleInfo = roleLabel[role || 'customer'] || { label: 'Admin', color: 'text-white/40' };

  // Avatar do perfil real
  const avatarUrl = profile?.avatar_url ?? null;
  const displayName = profile?.display_name || profile?.full_name || profile?.name || 'Admin';
  const initials = displayName.slice(0, 2).toUpperCase();

  const NavItemLink = ({ item }: { item: NavItem }) => (
    <li>
      <NavLink
        to={item.path}
        end={item.path === '/admin'}
        onClick={onMobileClose}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-xl transition-all duration-150 cursor-pointer whitespace-nowrap ${
            isCollapsed ? 'justify-center w-10 h-10 mx-auto' : 'px-3 py-2.5'
          } ${
            isActive
              ? 'font-500'
              : 'text-white/60 hover:bg-white/5 hover:text-white'
          }`
        }
        style={({ isActive }) =>
          isActive
            ? {
                backgroundColor: `${adminPrimary}28`,
                color: adminPrimary,
                ...(isCollapsed ? {} : { borderLeft: `3px solid ${adminPrimary}` }),
              }
            : {}
        }
        title={isCollapsed ? item.label : undefined}
      >
        {({ isActive }) => (
          <>
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i
                className={`${item.icon} text-[18px]`}
                style={isActive ? { color: adminPrimary } : undefined}
              />
            </div>
            {!isCollapsed && <span className="text-[14px] font-500">{item.label}</span>}
          </>
        )}
      </NavLink>
    </li>
  );

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-30 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      <aside
        className={`fixed left-0 top-0 h-full bg-[#0F0F1A] transition-all duration-300 z-40 flex flex-col
          ${isCollapsed ? 'w-[68px]' : 'w-[260px]'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="flex items-center h-[72px] px-4 border-b border-white/10 shrink-0">
          {!isCollapsed && (
            <div className="flex items-center gap-2.5 flex-1 overflow-hidden">
              <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center shrink-0">
                <i className="ri-shield-star-line text-white text-[16px]" />
              </div>
              <div className="overflow-hidden">
                <span className="text-[14px] font-700 text-white whitespace-nowrap">
                  Master<span className="text-violet-400">Admin</span>
                </span>
                <p className="text-[10px] text-white/40 whitespace-nowrap -mt-0.5">{platformName}</p>
              </div>
            </div>
          )}
          {isCollapsed && (
            <div className="w-full flex justify-center">
              <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
                <i className="ri-shield-star-line text-white text-[16px]" />
              </div>
            </div>
          )}
          {!isCollapsed && (
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={onMobileClose}
                className="lg:hidden w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-white/50 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
              <button
                onClick={onToggle}
                className="hidden lg:flex w-8 h-8 items-center justify-center rounded-lg hover:bg-white/10 text-white/50 transition-colors cursor-pointer"
              >
                <i className="ri-layout-left-2-line text-lg" />
              </button>
            </div>
          )}
        </div>

        {isCollapsed && (
          <div className="flex justify-center py-3 border-b border-white/10">
            <button
              onClick={onToggle}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-white/50 transition-colors cursor-pointer"
            >
              <i className="ri-layout-right-2-line text-lg" />
            </button>
          </div>
        )}

        {/* Card do usuário com avatar real */}
        {!isCollapsed && role && (
          <div className="mx-3 mt-3 mb-1 px-3 py-2 bg-white/5 border border-white/10 rounded-xl flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full overflow-hidden bg-violet-600/30 flex items-center justify-center shrink-0">
              {avatarUrl ? (
                <img src={avatarUrl} alt={displayName} className="w-7 h-7 object-cover object-top" />
              ) : (
                <span className="text-white text-[10px] font-700">{initials}</span>
              )}
            </div>
            <div className="overflow-hidden flex-1">
              <p className="text-[12px] text-white font-600 truncate">{displayName}</p>
              <p className={`text-[10px] font-600 ${roleInfo.color}`}>{roleInfo.label}</p>
            </div>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {!isCollapsed && (
            <p className="text-[10px] font-600 text-white/30 uppercase tracking-wider px-3 mb-2">Principal</p>
          )}
          <ul className="space-y-0.5">
            {visibleMain.map((item) => <NavItemLink key={item.path} item={item} />)}
          </ul>

          {visibleConfig.length > 0 && (
            <>
              <div className="my-4 border-t border-white/10" />
              {!isCollapsed && (
                <p className="text-[10px] font-600 text-white/30 uppercase tracking-wider px-3 mb-2">Sistema</p>
              )}
              <ul className="space-y-0.5">
                {visibleConfig.map((item) => <NavItemLink key={item.path} item={item} />)}
              </ul>
            </>
          )}

          <div className="my-4 border-t border-white/10" />

          {/* Voltar ao App — expandido */}
          {!isCollapsed && (
            <NavLink
              to="/dashboard"
              onClick={onMobileClose}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/50 hover:bg-white/5 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className="ri-arrow-left-line text-[18px]" />
              </div>
              <span className="text-[14px] font-500">Voltar ao App</span>
            </NavLink>
          )}

          {/* Voltar ao App — colapsado */}
          {isCollapsed && (
            <li className="list-none flex justify-center">
              <NavLink
                to="/dashboard"
                onClick={onMobileClose}
                title="Voltar ao App"
                className="w-10 h-10 flex items-center justify-center rounded-xl text-white/50 hover:bg-white/5 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-[18px]" />
              </NavLink>
            </li>
          )}
        </nav>

        <div className="px-3 pb-4 border-t border-white/10 pt-3 shrink-0">
          <button
            onClick={handleSignOut}
            className={`flex items-center gap-3 rounded-xl transition-all duration-150 cursor-pointer whitespace-nowrap text-white/50 hover:bg-white/5 hover:text-white w-full ${
              isCollapsed ? 'justify-center w-10 h-10 mx-auto' : 'px-3 py-2.5'
            }`}
            title={isCollapsed ? 'Sair' : undefined}
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-logout-box-r-line text-[18px]" />
            </div>
            {!isCollapsed && <span className="text-[14px] font-500">Sair</span>}
          </button>
        </div>
      </aside>
    </>
  );
};
